/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import * as React from 'react';
import styled from 'styled-components';
import { Headline, Layout, LayoutItem, TextLink } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import type { VariantConfig } from './PartnerBusinessCard';
import { PartnerContext } from '../PartnerProvider';
import { useTrackingManager } from '../useTrackingManager';
import { messages } from '../../i18n/messages';

interface PartnerBusinessCardAddressProps {
  variantConfig: VariantConfig;
}

const StyledHeadlineAsLink = styled.div`
  margin-bottom: 15px;
  > a {
    color: ${process.env.IS_LEGACY ? '#333' : '#FFF'};
    &:hover {
      color: ${process.env.IS_LEGACY ? '#4d4d4d' : 'hsla(216, 33%, 99%, 0.7)'};
    }
    text-decoration: none;
  }
`;

const AddressContainer = styled(LayoutItem)``;

export const ButtonAlignedLeft = styled(TextLink)`
  margin-bottom: 15px;

  text-align: left;
  span {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  text-decoration: none;
`;

export const PartnerBusinessCardAddress: React.FC<PartnerBusinessCardAddressProps> = ({
  variantConfig,
}): React.ReactElement => {
  const { partner } = React.useContext(PartnerContext);
  const trackingManager = useTrackingManager();

  const { isLinkedToPWS, hasOuterSpacing, isGoogleMapLink } =
    variantConfig.contactDetails || variantConfig;

  let { displayOfficialName, displayEmail, displayPhone } =
    variantConfig.contactDetails || variantConfig;

  displayOfficialName = (displayOfficialName && partner.officialName) as unknown as boolean;
  displayEmail = (displayEmail && partner.email) as unknown as boolean;
  displayPhone = (displayPhone && partner.phone) as unknown as boolean;

  const partnerName = displayOfficialName ? partner.officialName : partner.nonOfficialName;

  const containerMargin = hasOuterSpacing ? 'var(--spacing-relative-md)' : 0;
  const { headline = { headlineOrder: '2', headlineTag: 'h3' } } = variantConfig;
  const { headlineOrder, headlineTag } = headline;
  const dealerNameLinkAriaLabel = useI18n(messages.dealerNameLinkAriaLabel);

  return (
    <AddressContainer margin={[containerMargin]}>
      {partner && partner.partnerId !== '' && (
        <>
          <Headline
            tag={headlineTag}
            variant={{ type: 'headline', order: headlineOrder, weight: 'normal' }}
            data-testid="pbc-partner-name"
          >
            {isLinkedToPWS && partner.url ? (
              <StyledHeadlineAsLink>
                <a
                  data-testid="pbc-partner-name-link"
                  href={partner.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={dealerNameLinkAriaLabel}
                >
                  {partnerName}
                </a>
              </StyledHeadlineAsLink>
            ) : (
              <StyledHeadlineAsLink aria-label={dealerNameLinkAriaLabel}>
                {partnerName}
              </StyledHeadlineAsLink>
            )}
          </Headline>
          <Layout direction="column">
            {isGoogleMapLink && (
              <ButtonAlignedLeft
                variant="standalone"
                icon="geofence"
                iconPosition="leading"
                size="md"
                href={partner.directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackingManager.click(
                    'exit_link',
                    partner.directionsUrl,
                    'link',
                    `${partner.address[0]} ${partner.address[1]}`,
                    partnerName,
                    '1',
                    'location',
                  );
                }}
                data-testid="pbc-contact-directions"
              >
                {partner.address[0]}
                <br />
                {partner.address[1]}
              </ButtonAlignedLeft>
            )}

            {displayPhone && (
              <ButtonAlignedLeft
                href={`tel:${partner.phone}`}
                data-testid="pbc-contact-phone"
                variant="standalone"
                icon="voice-call"
                iconPosition="leading"
                size="md"
                onClick={() => {
                  trackingManager.click(
                    'call',
                    partner.directionsUrl,
                    'link',
                    'Anrufen',
                    partnerName,
                    '2',
                    'phone',
                  );
                }}
              >
                {partner.phone}
              </ButtonAlignedLeft>
            )}
            {partner.email && displayEmail && (
              <ButtonAlignedLeft
                data-testid="pbc-contact-email"
                href={`mailto:${partner.email}`}
                variant="standalone"
                icon="direct-mail"
                iconPosition="leading"
                size="md"
                onClick={() => {
                  trackingManager.click(
                    'email',
                    partner.directionsUrl,
                    'link',
                    partner.email || '',
                    partnerName,
                    '3',
                    'mail',
                  );
                }}
              >
                {partner.email}
              </ButtonAlignedLeft>
            )}
          </Layout>
        </>
      )}
    </AddressContainer>
  );
};
